import type { ReactNode } from "react"
import { FiExternalLink, FiMapPin } from "react-icons/fi"

/**
 * The location fields an approver / verifier needs to judge a punch.
 *
 * Every line is always rendered — a missing value shows "Not captured" rather
 * than the line disappearing, so an approver can tell "no data" apart from "the
 * screen didn't show it". Distances are stored in metres and shown in km.
 */
export interface AttendanceLocationFields {
    marking_type?: string | null
    check_in_time?: string | null
    check_out_time?: string | null
    check_in_latitude?: number | string | null
    check_in_longitude?: number | string | null
    check_out_latitude?: number | string | null
    check_out_longitude?: number | string | null
    check_in_address?: string | null
    check_out_address?: string | null
    reporting_latitude?: number | string | null
    reporting_longitude?: number | string | null
    distance_from_reporting?: number | string | null
    check_out_distance_from_reporting?: number | string | null
}

const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

const coordsOf = (lat: unknown, lng: unknown): { lat: number; lng: number } | null => {
    const a = toNumber(lat)
    const b = toNumber(lng)
    if (a === null || b === null) return null
    // (0, 0) is the "nothing sent" value from older app builds, not a real fix.
    if (a === 0 && b === 0) return null
    return { lat: a, lng: b }
}

/** Metres → "1.23 km", or null when not a number. */
export const formatDistanceKm = (metres: unknown): string | null => {
    const n = toNumber(metres)
    return n === null ? null : `${(n / 1000).toFixed(2)} km`
}

/** "lat, lng" at 6 decimals, or null. */
export const formatLatLng = (lat: unknown, lng: unknown): string | null => {
    const c = coordsOf(lat, lng)
    return c ? `${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}` : null
}

const mapsUrl = (c: { lat: number; lng: number }) =>
    `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`

const NotCaptured = () => <span className="text-gray-400 italic">Not captured</span>

const CoordValue = ({ lat, lng }: { lat: unknown; lng: unknown }) => {
    const c = coordsOf(lat, lng)
    if (!c) return <NotCaptured />
    return (
        <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-gray-900">{c.lat.toFixed(6)}, {c.lng.toFixed(6)}</span>
            <a
                href={mapsUrl(c)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:underline"
                title="Open in Google Maps"
            >
                Map <FiExternalLink className="w-3 h-3" />
            </a>
        </span>
    )
}

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
    <div>
        <span className="text-gray-600">{label}:</span>
        <div className="mt-0.5 break-words">{children}</div>
    </div>
)

interface Props {
    record: AttendanceLocationFields
    /** Outer wrapper classes, so each screen keeps its own spacing. */
    className?: string
    title?: string
}

export default function AttendanceLocationDetails({
    record,
    className = "p-3 bg-gray-50 rounded",
    title = "Location Details",
}: Props) {
    const hasCheckInCoords = coordsOf(record.check_in_latitude, record.check_in_longitude) !== null
    const hasCheckOutCoords = coordsOf(record.check_out_latitude, record.check_out_longitude) !== null
    const hasReporting = coordsOf(record.reporting_latitude, record.reporting_longitude) !== null
    const isManual = (record.marking_type || "").toLowerCase() === "manual"
    const checkInDistance = formatDistanceKm(record.distance_from_reporting)
    const checkOutDistance = formatDistanceKm(record.check_out_distance_from_reporting)

    const hint = isManual && !hasCheckInCoords && !hasCheckOutCoords
        ? "Manual mark — no location captured."
        : !hasReporting && (hasCheckInCoords || hasCheckOutCoords) && (!checkInDistance || !checkOutDistance)
            ? "No reporting location set for this user — distance can't be calculated."
            : null

    return (
        <div className={className}>
            <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
            {hint && <p className="text-xs text-amber-700 mb-2">{hint}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs md:text-sm">
                <Row label="Check-in Lat/Long">
                    <CoordValue lat={record.check_in_latitude} lng={record.check_in_longitude} />
                </Row>
                <Row label="Check-out Lat/Long">
                    {record.check_out_time || hasCheckOutCoords
                        ? <CoordValue lat={record.check_out_latitude} lng={record.check_out_longitude} />
                        : <span className="text-gray-400 italic">Not checked out</span>}
                </Row>
                <Row label="Check-in Address">
                    {record.check_in_address ? (
                        <span className="flex items-start gap-1.5">
                            <FiMapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-900">{record.check_in_address}</span>
                        </span>
                    ) : <NotCaptured />}
                </Row>
                <Row label="Check-out Address">
                    {record.check_out_address ? (
                        <span className="flex items-start gap-1.5">
                            <FiMapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-900">{record.check_out_address}</span>
                        </span>
                    ) : <NotCaptured />}
                </Row>
                <Row label="Check-in Distance">
                    {checkInDistance
                        ? <span className="font-medium text-gray-900">{checkInDistance}</span>
                        : <NotCaptured />}
                </Row>
                <Row label="Check-out Distance">
                    {checkOutDistance
                        ? <span className="font-medium text-gray-900">{checkOutDistance}</span>
                        : <NotCaptured />}
                </Row>
            </div>
        </div>
    )
}
